import React, { useState } from "react";
import {
  Lock,
  Eye,
  EyeOff,
  Loader2,
  ShieldCheck,
  ArrowLeft,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { motion } from "framer-motion";
import { OneDeskUpdatePassword } from "@repo/api-client";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !formData.oldPassword ||
      !formData.newPassword ||
      !formData.confirmPassword
    ) {
      toast.error("Please fill in all fields");
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (formData.newPassword.length < 6) {
      toast.error("Password must be at least 6 characters long");
      return;
    }

    setIsLoading(true);
    try {
      const response = await OneDeskUpdatePassword({
        OldPasswordHash: formData.oldPassword,
        NewPasswordHash: formData.newPassword,
        NewSalt: "123456",
      });

      if (response.Success) {
        toast.success(
          response.Message || "Credentials synchronized successfully!",
        );
        setTimeout(() => {
          navigate("/dashboard");
        }, 1500);
      } else {
        toast.error(
          response.Message ||
            "Synchronization failed. Please verify credentials.",
        );
      }
    } catch (error) {
      console.error("Reset password error:", error);
      toast.error("Critical connection failure. Terminal sync lost.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto py-4 md:py-10 px-2 animate-fadeIn">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate(-1)}
          className="p-2.5 text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-white dark:hover:bg-[#1b254b] hover:shadow-sm rounded-xl transition-all border border-transparent hover:border-slate-200 dark:hover:border-white/10"
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 tracking-tight">
            Access Control
          </h1>
          <p className="text-slate-500 dark:text-slate-500 font-semibold uppercase tracking-widest text-[10px] mt-0.5">
            Personnel Management / Security
          </p>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-[#111c44] rounded-2xl p-5 md:p-10 shadow-sm border border-slate-200 dark:border-white/10 relative overflow-hidden"
      >
        {/* Decorative background element */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50/50 dark:bg-blue-900/10 rounded-bl-[80px] -mr-12 -mt-12 transition-colors" />

        <div className="flex items-center gap-4 mb-8 relative z-10">
          <div className="p-3 bg-slate-900 dark:bg-[#1b254b] rounded-xl text-white shadow-sm">
            <ShieldCheck size={24} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100 tracking-tight">
              Reset Root Password
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">
              Update administrative access tokens.
            </p>
          </div>
        </div>

        <form
          onSubmit={handleResetPassword}
          className="space-y-6 relative z-10"
        >
          <div className="space-y-6">
            {/* Old Password */}
            <div className="space-y-2">
              <label className="text-xs font-semibold text-slate-500 dark:text-slate-500 uppercase tracking-widest pl-1">
                Current Password
              </label>
              <div className="relative group">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-600 group-focus-within:text-blue-600 dark:group-focus-within:text-blue-400 transition-colors">
                  <Lock size={18} />
                </div>
                <input
                  type={showOldPassword ? "text" : "password"}
                  name="oldPassword"
                  value={formData.oldPassword}
                  onChange={handleChange}
                  className="w-full bg-slate-50 dark:bg-[#1b254b]/50 border border-slate-200 dark:border-white/10 rounded-xl px-11 py-3 focus:bg-white dark:focus:bg-[#0b1437] focus:ring-4 focus:ring-blue-600/10 dark:focus:ring-white/5 focus:border-blue-500 dark:focus:border-white/20 outline-none transition-all duration-300 font-medium text-slate-800 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-600"
                  placeholder="Enter old password"
                />
                <button
                  type="button"
                  onClick={() => setShowOldPassword(!showOldPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showOldPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div className="space-y-2">
              <label className="text-xs font-semibold text-slate-500 dark:text-slate-500 uppercase tracking-widest pl-1">
                New Password
              </label>
              <div className="relative group">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-600 group-focus-within:text-blue-600 dark:group-focus-within:text-blue-400 transition-colors">
                  <Lock size={18} />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleChange}
                  className="w-full bg-slate-50 dark:bg-[#1b254b]/50 border border-slate-200 dark:border-white/10 rounded-xl px-11 py-3 focus:bg-white dark:focus:bg-[#0b1437] focus:ring-4 focus:ring-blue-600/10 dark:focus:ring-white/5 focus:border-blue-500 dark:focus:border-white/20 outline-none transition-all duration-300 font-medium text-slate-800 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-600"
                  placeholder="Enter new password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <label className="text-xs font-semibold text-slate-500 dark:text-slate-500 uppercase tracking-widest pl-1">
                Confirm New Password
              </label>
              <div className="relative group">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-600 group-focus-within:text-blue-600 dark:group-focus-within:text-blue-400 transition-colors">
                  <Lock size={18} />
                </div>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="w-full bg-slate-50 dark:bg-[#1b254b]/50 border border-slate-200 dark:border-white/10 rounded-xl px-11 py-3 focus:bg-white dark:focus:bg-[#0b1437] focus:ring-4 focus:ring-blue-600/10 dark:focus:ring-white/5 focus:border-blue-500 dark:focus:border-white/20 outline-none transition-all duration-300 font-medium text-slate-800 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-600"
                  placeholder="Confirm new password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showConfirmPassword ? (
                    <EyeOff size={18} />
                  ) : (
                    <Eye size={18} />
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 dark:bg-amber-900/10 rounded-xl p-4 flex gap-3 border border-amber-200/60 dark:border-white/5">
            <AlertCircle
              size={18}
              className="text-amber-600 dark:text-amber-500 shrink-0 mt-0.5"
            />
            <div>
              <p className="text-xs text-amber-800 dark:text-amber-300 font-bold uppercase tracking-wider mb-1">
                Security Enforcement
              </p>
              <p className="text-xs text-amber-700 dark:text-amber-400 font-medium leading-relaxed">
                Passwords must be at least 6 characters and should contain a mix
                of letters, numbers, and symbols.
              </p>
            </div>
          </div>

          <div className="pt-4 flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="w-full sm:flex-1 h-12 rounded-xl font-bold text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-[#1b254b] border border-slate-200 dark:border-white/10 transition-all uppercase tracking-widest text-xs active:scale-[0.98]"
            >
              Abort
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full sm:flex-1 h-12 bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600 text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-all shadow-sm active:scale-[0.98] disabled:bg-slate-200 dark:disabled:bg-slate-800 disabled:text-slate-400 dark:disabled:text-slate-600 disabled:shadow-none uppercase tracking-widest text-xs"
            >
              {isLoading ? (
                <Loader2 className="animate-spin" size={18} />
              ) : (
                <>
                  Save Changes
                  <CheckCircle2 size={16} />
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default ResetPassword;
